import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router";
import { listMyTrainingGroups, type TrainingGroupRef } from "@/api";
import { useAppState } from "@/app";
import { loadLastTeam, resolveSelectedGroupUuid, saveLastTeam } from "./teamGroupSelection";
import { useAsyncList } from "./useAsyncList";

// 팀 스코프 browse 페이지(Training·Evaluation)가 함께 쓰는 그룹 선택 경계.
//
// 소유하는 것:
// - 그룹 피커 소스(내가 멤버인 모든 training 그룹)와 드롭다운 옵션 변환.
// - 유효 선택 해석, URL(?group=) 보존, 마지막으로 선택한 팀 기억.
//
// 소유하지 않는 것:
// - 드롭다운을 그릴 위치(페이지 헤더 / 탭 필터 줄)와 그 라벨.
// - 선택된 그룹으로 조회할 내용(각 패널의 테이블 source 가 정한다).
export type TeamGroupSelection = {
  options: readonly { value: string; label: string }[];
  selectedGroupUuid: string | null;
  hasGroups: boolean;
  // 그룹 목록이 아직 오지 않아 selectedGroupUuid 가 null 인 상태. "그룹이 없다"와 구분한다. 그래서
  // team 스코프 패널이 빈 상태 대신 로딩을 유지한다.
  isResolvingGroups: boolean;
  onGroupChange: (next: string) => void;
};

const groupSearchParamKey = "group";

// 그룹 목록은 team 스코프에서만 필요하다. me 스코프에서 미리 받으면 페이지에 들어올 때마다 쓰지
// 않는 round-trip 이 하나 늘어난다(rules.md §7).
//
// finalizeGroupCommit: 그룹 *전환* URL 커밋에 함께 넣을 정리(예: group 종속 필터 키 제거).
// 어떤 키를 지울지는 페이지가 안다. 같은 커밋이어야 오래된 조합의 히스토리 엔트리가 생기지 않는다.
// 그룹이 없던 URL 에 첫 그룹을 쓰는 확정 커밋에는 넣지 않는다. 버릴 이전 맥락이 없기 때문이다.
export function useTeamGroupSelection(
  enabled: boolean = true,
  finalizeGroupCommit: (params: URLSearchParams) => URLSearchParams = (params) => params,
): TeamGroupSelection {
  const [searchParams, setSearchParams] = useSearchParams();
  const groupParam = searchParams.get(groupSearchParamKey);

  // 마지막 팀 기억은 uid 단위라 현재 사용자 uid 가 필요하다.
  const { state } = useAppState();
  const uid = state.currentUser?.uid ?? null;

  // 그룹 피커 소스. 내가 속한 모든 training 그룹이다(browse 표면이라 연결 여부와 무관하다).
  const groupsFetcher = useCallback(
    (): Promise<readonly TrainingGroupRef[]> =>
      enabled ? listMyTrainingGroups() : Promise.resolve<readonly TrainingGroupRef[]>([]),
    [enabled],
  );
  const groups = useAsyncList<TrainingGroupRef>(groupsFetcher);

  // 유효 선택 = 딥링크(?group=) → 기억된 마지막 팀 → 첫 그룹 순서. 그룹이 없으면 null 이다.
  const selectedGroupUuid = useMemo(
    () => resolveSelectedGroupUuid(groups.items, groupParam, loadLastTeam(uid)),
    [groupParam, groups.items, uid],
  );

  useEffect(() => {
    if (enabled && groups.status === "loading") {
      return;
    }
    const nextGroup = enabled ? selectedGroupUuid : null;
    if (nextGroup === groupParam) {
      return;
    }
    // 그룹이 없던 URL 에 첫 그룹을 쓰는 것은 맥락 변경이 아니라 확정이다. 그래서 종속 선택을 버리지
    // 않는다(me 스코프에 남은 group 을 지우는 커밋도 같다). PolicyTableSource.setQuery 도 선택
    // 시드에 같은 구분을 쓴다.
    const isGroupSwitch = groupParam !== null && nextGroup !== null;
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (nextGroup === null) {
          params.delete(groupSearchParamKey);
        } else {
          params.set(groupSearchParamKey, nextGroup);
        }
        return isGroupSwitch ? finalizeGroupCommit(params) : params;
      },
      { replace: true },
    );
  }, [enabled, finalizeGroupCommit, groupParam, groups.status, selectedGroupUuid, setSearchParams]);

  const options = useMemo(
    () => groups.items.map((group) => ({ value: group.group_uuid, label: group.name })),
    [groups.items],
  );

  const onGroupChange = useCallback(
    (next: string) => {
      // 사용자가 고른 것은 항상 전환이라 finalizeGroupCommit 을 조건 없이 적용한다.
      // 명시적으로 고른 팀만 기억한다(자동 선택된 첫 그룹과 딥링크는 기억하지 않는다).
      saveLastTeam(uid, next);
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set(groupSearchParamKey, next);
        return finalizeGroupCommit(params);
      });
    },
    [setSearchParams, uid, finalizeGroupCommit],
  );

  return {
    options,
    selectedGroupUuid,
    hasGroups: groups.items.length > 0,
    isResolvingGroups: enabled && groups.status === "loading",
    onGroupChange,
  };
}
